import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  buildSettingsExportFileName,
  loadWorkspaceExportData,
  triggerJsonDownload,
} from "@/lib/settingsDataExport";

type PrimaryWorkspace = {
  id: string;
  display_name: string | null;
  workspace_label: string | null;
  account_tier: string | null;
};

export const AccountIdentityCard = ({ primary }: { primary: PrimaryWorkspace }) => {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setDisplayName(primary.display_name ?? "");
  }, [primary.id, primary.display_name]);

  const saveAccountName = async () => {
    const trimmed = displayName.trim();
    if (!trimmed) return toast.error("Display name can't be empty");
    setSavingName(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: trimmed })
      .eq("id", primary.id);
    setSavingName(false);
    if (error) return toast.error(error.message);
    toast.success("Account name saved");
  };

  const exportData = async () => {
    if (!user) return;
    setExporting(true);
    try {
      const payload = await loadWorkspaceExportData(supabase, {
        workspaceId: primary.id,
        userId: user.id,
        environment: getStripeEnvironment(),
        userEmail: user.email,
        workspaceLabel: primary.workspace_label,
        displayName: primary.display_name,
        personaId: primary.id,
      });
      triggerJsonDownload(buildSettingsExportFileName(primary.workspace_label), payload);
      toast.success("Account export downloaded");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader><CardTitle>Account</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <Badge variant="secondary">{primary.account_tier}</Badge>
          <span className="text-sm text-muted-foreground truncate min-w-0">{user?.email}</span>
        </div>
        <div>
          <Label htmlFor="account-display-name">Display name</Label>
          <Input
            id="account-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div>
          <Button onClick={saveAccountName} disabled={savingName}>
            {savingName ? "Saving…" : "Save"}
          </Button>
        </div>
        <div className="rounded-md border border-border bg-muted/30 p-4 space-y-3">
          <div className="space-y-1">
            <h2 className="text-sm font-medium text-foreground">Export account data</h2>
            <p className="text-xs text-muted-foreground">
              Download a JSON file with your excerpts, quote-generator settings, brand colors,
              Readwise highlights, usage counters, and current billing metadata for your primary workspace.
            </p>
            <p className="text-xs text-muted-foreground">
              This export does not close your account, remove billing records, or delete anything.
            </p>
          </div>
          <Button variant="outline" onClick={exportData} disabled={exporting}>
            {exporting ? "Preparing JSON export…" : "Download JSON export"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
